import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { messagingEndpointsApi, onboardingApi, waitlistApi } from "./api/routes";
import { setAccessTokenGetter } from "./api/client";
import { auth0LoginParams, getAccessTokenOrLogout } from "./auth/auth0Config";
import { MascotSprite } from "./components/BrandArtwork";
import { Button, Input, Select, TextArea, Toast } from "./components/ui/Controls";
import TimeZoneCombo from "./components/ui/TimeZoneCombo";
import { DISTANCE_TYPE_OPTIONS, normalizeDistanceType } from "./domain/distanceTypes";
import { LEGAL_VERSION, SMS_DISCLOSURE_VERSION } from "./LegalPages";

const emptyUser = {
  name: "",
  phone: "",
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  preferredDistance: "HALF_MARATHON",
  planTier: "beta",
};

const emptyAthleteProfile = {
  trainingExperience: [],
  maxMileage: 0,
  availability: {},
  trainingBias: "NORMAL",
};

const weekdayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const availabilityExamples = {
  Monday: "Usually free before work, around 6-7am. Good day for an easy run, not a workout.",
  Tuesday: "Can train after school drop-off, maybe 45-60 minutes. Track is nearby if needed.",
  Wednesday: "Work meetings run late, so this is usually a rest day or 20 minutes very easy.",
  Thursday: "Best quality workout day. I can do 60-75 minutes after work if planned ahead.",
  Friday: "Flexible lunch window, but I prefer something light because weekends are busy.",
  Saturday: "Long-run day. I can start early and have up to 2 hours most weeks.",
  Sunday: "Family day. Maybe short recovery run, but not reliable every week.",
};

const trainingBiasOptions = [
  {
    value: "CAUTIOUS",
    title: "Durability First",
    description: "Progress slowly, protect recovery, and bias toward consistency. Best if you are injury-prone, returning from time off, or balancing a lot of life stress.",
  },
  {
    value: "NORMAL",
    title: "Steady Builder",
    description: "Use balanced progression with enough challenge to improve while keeping recovery and missed-workout flexibility in the plan.",
  },
  {
    value: "AGGRESSIVE",
    title: "Performance Push",
    description: "Accept a little more training stress when the context supports it. Best for experienced runners with reliable recovery and clear performance goals.",
  },
];

const prDistanceTypes = {
  "5k": "FIVE_K",
  "10k": "TEN_K",
  "Half Marathon": "HALF_MARATHON",
  Marathon: "MARATHON",
};

const onboardingDistanceOptions = DISTANCE_TYPE_OPTIONS.filter((option) => option.value !== "OFF_SEASON");

const onboardingSteps = [
  { key: "basics", label: "Basics", mascot: "wave" },
  { key: "background", label: "Background", mascot: "jump" },
  { key: "injuries", label: "Injuries", mascot: "run" },
  { key: "availability", label: "Availability", mascot: "jump" },
  { key: "preferences", label: "Preferences", mascot: "wave" },
  { key: "prs", label: "PRs", mascot: "run" },
  { key: "messaging", label: "Messaging", mascot: "wave" },
];

const coachBuildSignals = [
  "Collecting your training context",
  "Finding the right coaching guardrails",
  "Preparing your first coach memory",
];

const finalJobStatuses = new Set(["SUCCEEDED", "FAILED", "TIMED_OUT"]);

let injuryDraftId = 0;

function createInjuryDraft(injury = {}) {
  const localId = injury.localId || `injury-${injuryDraftId}`;
  injuryDraftId += 1;
  return {
    localId,
    injuryId: injury.injuryId || null,
    canonicalArea: injury.canonicalArea || injury.bodyLocation || injury.description || "",
    bodyLocation: injury.bodyLocation || "",
    description: injury.description || "",
    severity: (injury.severity || "MILD").toLowerCase(),
    status: (injury.status || (injury.endDate ? "RESOLVED" : "ONGOING")).toLowerCase(),
    startDate: injury.startDate || "",
    endDate: injury.endDate || "",
  };
}

function textBlockToList(value) {
  const trimmed = value.trim();
  return trimmed ? [trimmed] : [];
}

function preferenceRecords(value) {
  return textBlockToList(value).map((summary) => ({
    normalizedKey: `TRAINING|${summary.trim().toLowerCase().replace(/\s+/g, " ")}`,
    summary,
    scope: "TRAINING",
    polarity: /^(avoid|dislike)|do not like/i.test(summary) ? "AVOIDS" : "PREFERS",
    strength: "MODERATE",
    status: "ACTIVE",
    confidence: 1,
  }));
}

function objectFromPairs(pairs) {
  return Object.fromEntries(
    Object.entries(pairs)
      .map(([key, value]) => [key, value.trim()])
      .filter(([, value]) => value)
  );
}

function getErrorMessage(error, fallback) {
  return error?.displayMessage || error?.message || fallback;
}

function OptionCard({ option, selected, onSelect }) {
  return (
    <button type="button" className={`choice-card ${selected ? "selected" : ""}`} onClick={onSelect}>
      <strong>{option.title}</strong>
      <span>{option.description}</span>
    </button>
  );
}

function normalizePhone(value) {
  const cleaned = (value || "").trim();
  if (!cleaned) return "";
  const digits = cleaned.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits;
  if (digits.length === 10) return `+1${digits}`;
  return digits;
}

function smsEndpointId(phone) {
  const digits = normalizePhone(phone).replace(/\D/g, "");
  return digits ? `sms-${digits}` : "";
}

function WaitlistForm({ initialEmail = "" }) {
  const [email, setEmail] = useState(initialEmail);
  const [website, setWebsite] = useState("");
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState("");

  const joinWaitlist = async (event) => {
    event.preventDefault();
    setJoining(true);
    setError("");
    try {
      await waitlistApi.join({ email: email.trim(), website, source: "website-beta-gate" });
      setJoined(true);
    } catch (e) {
      setError(getErrorMessage(e, "Could not join the waitlist"));
    } finally {
      setJoining(false);
    }
  };

  if (joined) {
    return (
      <div className="beta-waitlist-confirmation" role="status">
        <strong>You are on the list.</strong>
        <span>We will email you when another Chara beta spot opens.</span>
      </div>
    );
  }

  return (
    <form className="beta-waitlist-form" onSubmit={joinWaitlist}>
      <Input label="Email address" type="email" value={email} onChange={setEmail} placeholder="runner@example.com" />
      <input
        className="beta-waitlist-honeypot"
        type="text"
        value={website}
        onChange={(event) => setWebsite(event.target.value)}
        tabIndex="-1"
        autoComplete="off"
        aria-hidden="true"
      />
      <Button disabled={joining || !email.trim()}>{joining ? "Joining..." : "Join the waitlist"}</Button>
      {error ? <p className="beta-waitlist-error" role="alert">{error}</p> : null}
    </form>
  );
}

function LegalLinkFooter() {
  return (
    <footer className="site-legal-links">
      <a href="/privacy">Privacy Policy</a>
      <a href="/terms">Terms and Conditions</a>
    </footer>
  );
}

function BetaAccessGate({ accessCode, setAccessCode, redeemAccessCode, redeemingAccess, email }) {
  return (
    <section className="onboarding-wizard">
      <div className="wizard-card wizard-card--gate">
        <div className="section-kicker">Closed beta</div>
        <MascotSprite variant="wave" className="wizard-mascot" />
        <h1>Your coach is in a small training group.</h1>
        <p className="step-copy">
          Chara is not fully public yet. We are bringing runners in carefully while the coaching system keeps learning. If you have a beta code, enter it to begin onboarding.
        </p>
        <div className="beta-gate-grid">
          <div className="beta-gate-panel beta-gate-panel--invite">
            <div className="section-kicker">I have an invite</div>
            <h2>Unlock beta onboarding.</h2>
            <Input label="Beta access code" value={accessCode} onChange={setAccessCode} placeholder="Enter your private beta code" />
            <Button onClick={redeemAccessCode} disabled={redeemingAccess || !accessCode.trim()}>
              {redeemingAccess ? "Checking..." : "Continue to onboarding"}
            </Button>
          </div>
          <div className="beta-gate-panel">
            <div className="section-kicker">No code yet?</div>
            <h2>Raise your hand for the next opening.</h2>
            <p>Join the interest list. We will reach out as the beta expands.</p>
            <WaitlistForm initialEmail={email} />
          </div>
        </div>
      </div>
    </section>
  );
}

function OnboardingShell({ auth }) {
  const [user, setUser] = useState(emptyUser);
  const [availability, setAvailability] = useState(() =>
    Object.fromEntries(weekdayOrder.map((day) => [day, ""]))
  );
  const [preferencesText, setPreferencesText] = useState("");
  const [trainingExperienceText, setTrainingExperienceText] = useState("");
  const [maxMileage, setMaxMileage] = useState("0");
  const [injuries, setInjuries] = useState([]);
  const [trainingBias, setTrainingBias] = useState("NORMAL");
  const [prTimes, setPrTimes] = useState({ "5k": "", "10k": "", "Half Marathon": "", Marathon: "" });
  const [prDates, setPrDates] = useState({});
  const [prContexts, setPrContexts] = useState({});
  const [messagingChoice, setMessagingChoice] = useState("TELEGRAM");
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsConnecting, setSmsConnecting] = useState(false);
  const [telegramLink, setTelegramLink] = useState(null);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [accessResult, setAccessResult] = useState(null);
  const [redeemingAccess, setRedeemingAccess] = useState(false);
  const [buildStatus, setBuildStatus] = useState("Preparing your coach...");
  const [buildError, setBuildError] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [toast, setToast] = useState(null);
  const [signupLegalAccepted, setSignupLegalAccepted] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const { isAuthenticated, isLoading, loginWithRedirect, logout, getAccessTokenSilently, user: authUser } = auth;
  const isBuildingProfile = window.location.pathname.startsWith("/onboarding/building");
  const buildJobGroupId = new URLSearchParams(window.location.search).get("jobGroupId");

  const completion = useMemo(() => {
    return Math.round(((stepIndex + 1) / onboardingSteps.length) * 100);
  }, [stepIndex]);

  const currentStep = onboardingSteps[stepIndex];
  const accessAccepted = Boolean(accessResult?.accepted || accessResult?.accessLevel);
  const smsReadyToFinish = messagingChoice !== "SMS" || Boolean(normalizePhone(user.phone) && smsConsent);
  const canFinishProfile = accessAccepted && legalAccepted && smsReadyToFinish;

  const nextStep = useMemo(() => {
    if (!legalAccepted) return "Review and accept the Terms and Privacy Policy before saving your coaching profile.";
    if (!user.name.trim()) return "Add your name so Chara can personalize coaching.";
    if (!trainingExperienceText.trim()) return "Add a short training background.";
    if (!weekdayOrder.some((day) => Boolean(availability[day]?.trim()))) return "Add at least one training window.";
    if (!profileSaved) return "Save your profile so Chara can remember the coaching context.";
    if (messagingChoice === "SMS" && !smsConsent) return "Accept SMS messaging terms before using SMS for coaching.";
    if (messagingChoice === "TELEGRAM" && !telegramStatus?.connected) return "Connect Telegram when you want text-first coaching.";
    return "You are ready to start training.";
  }, [availability, legalAccepted, messagingChoice, profileSaved, smsConsent, telegramStatus, trainingExperienceText, user.name]);

  const showToast = (message, tone = "success") => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setAccessTokenGetter(null);
      return;
    }
    setAccessTokenGetter(() => getAccessTokenOrLogout(getAccessTokenSilently, logout));
    return () => setAccessTokenGetter(null);
  }, [getAccessTokenSilently, isAuthenticated, logout]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await onboardingApi.getMe();
      hydrateState(response.data || {});
      setProfileSaved(true);
    } catch (e) {
      const message = getErrorMessage(e, "Failed to load onboarding profile");
      setLoadError(message);
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    load();
  }, [isAuthenticated, load]);

  useEffect(() => {
    if (!telegramLink || telegramStatus?.connected) return undefined;
    const interval = setInterval(async () => {
      try {
        const response = await onboardingApi.telegramStatus();
        setTelegramStatus(response.data || null);
      } catch (e) {
        // ignore background poll errors
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [telegramLink, telegramStatus]);

  useEffect(() => {
    if (!isAuthenticated || !isBuildingProfile) return undefined;
    if (!buildJobGroupId) {
      setBuildError("Missing onboarding job. Please return to onboarding and finish your profile again.");
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await onboardingApi.jobStatus(buildJobGroupId);
        if (cancelled) return;
        const status = response.data?.status;
        const childJobs = response.data?.jobs || [];
        const failedJob = childJobs.find((job) => job.status === "FAILED" || job.status === "TIMED_OUT");
        if (failedJob) {
          setBuildError(response.data?.errorMessage || failedJob.errorMessage || "The coach profile job did not finish. Please try finishing your profile again.");
          return;
        }
        if (status === "COMPLETE" || status === "SUCCEEDED" || (childJobs.length > 0 && childJobs.every((job) => job.status === "SUCCEEDED"))) {
          window.location.assign("/?panel=athlete");
          return;
        }
        if (status && finalJobStatuses.has(status)) {
          setBuildError("The coach profile job ended before the athlete profile was ready.");
          return;
        }
        setBuildStatus("Chara is reading your training history and shaping the first coach profile...");
      } catch (e) {
        if (!cancelled) {
          setBuildError(getErrorMessage(e, "Failed to check coach profile progress"));
        }
      }
    };
    poll();
    const interval = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [buildJobGroupId, isAuthenticated, isBuildingProfile]);

  const hydrateState = (data) => {
    const payloadUser = data.user || {};
    const athleteProfile = data.athleteProfile || {};
    const athleteMemory = data.athleteMemory || {};
    setUser((current) => ({
      ...current,
      ...payloadUser,
      preferredDistance: normalizeDistanceType(payloadUser.preferredDistance || current.preferredDistance),
    }));
    setLegalAccepted(Boolean(payloadUser.termsAcceptedAt && payloadUser.privacyAcceptedAt));
    setAvailability(Object.fromEntries(weekdayOrder.map((day) => [day, athleteProfile.availability?.[day] || ""])));
    setPreferencesText((athleteMemory.preferences || []).map((preference) => preference.summary).filter(Boolean).join("\n\n"));
    setTrainingExperienceText((athleteProfile.trainingExperience || []).join("\n\n"));
    setMaxMileage(String(athleteProfile.maxMileage || 0));
    setTrainingBias(athleteProfile.trainingBias || "NORMAL");
    if (Array.isArray(athleteProfile.personalRecords)) {
      const nextPrs = {};
      const nextDates = {};
      const nextContexts = {};
      athleteProfile.personalRecords.forEach((record) => {
        const label = Object.entries(prDistanceTypes).find(([, value]) => value === record.distance)?.[0];
        if (!label) return;
        nextPrs[label] = record.time || "";
        nextDates[label] = record.date || "";
        nextContexts[label] = record.context || "";
      });
      setPrTimes((current) => ({ ...current, ...nextPrs }));
      setPrDates(nextDates);
      setPrContexts(nextContexts);
    }
    setInjuries(
      (athleteMemory.injuries || []).map((injury) => createInjuryDraft(injury))
    );
    setTelegramStatus(data.telegram || null);
    setMessagingChoice(payloadUser.preferredMessagingChannel === "SMS" ? "SMS" : "TELEGRAM");
    const hydratedAccess = data.access || (data.accessLevel ? {
      accepted: true,
      accessLevel: data.accessLevel,
      publicAccessEnabled: data.publicAccessEnabled,
    } : null);
    setAccessResult(hydratedAccess);
  };

  const save = async (completeOnboarding = false) => {
    if (!legalAccepted) {
      showToast("Accept the Terms and Privacy Policy before saving your profile", "error");
      return null;
    }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const payload = {
        completeOnboarding,
        user: {
          ...user,
          phone: messagingChoice === "SMS" ? normalizePhone(user.phone) : user.phone,
          preferredDistance: normalizeDistanceType(user.preferredDistance),
          preferredMessagingChannel: messagingChoice,
          termsAcceptedAt: user.termsAcceptedAt || now,
          privacyAcceptedAt: user.privacyAcceptedAt || now,
          legalAcceptanceVersion: LEGAL_VERSION,
          smsOptInAt: messagingChoice === "SMS" && smsConsent ? (user.smsOptInAt || now) : user.smsOptInAt,
          smsOptInSource: messagingChoice === "SMS" && smsConsent ? "ONBOARDING" : user.smsOptInSource,
          smsOptInDisclosureVersion: messagingChoice === "SMS" && smsConsent
            ? SMS_DISCLOSURE_VERSION
            : user.smsOptInDisclosureVersion,
        },
        athleteProfile: {
          ...emptyAthleteProfile,
          trainingExperience: textBlockToList(trainingExperienceText),
          maxMileage: Number(maxMileage || 0),
          availability: objectFromPairs(availability),
          personalRecords: Object.entries(prDistanceTypes)
            .map(([label, distance]) => ({
              distance,
              time: prTimes[label]?.trim() || "",
              date: prDates[label]?.trim() || null,
              context: prContexts[label]?.trim() || null,
            }))
            .filter((record) => record.time),
          trainingBias,
        },
        athleteMemory: {
          preferences: preferenceRecords(preferencesText),
          injuries: injuries
            .map((injury) => ({
              injuryId: injury.injuryId || null,
              canonicalArea: injury.canonicalArea?.trim() || injury.bodyLocation?.trim() || injury.description?.trim() || "",
              bodyLocation: injury.bodyLocation?.trim() || null,
              description: injury.description?.trim() || "",
              severity: (injury.severity || "mild").toUpperCase(),
              status: (injury.status || "ongoing").toUpperCase(),
              startDate: injury.startDate?.trim() || null,
              endDate: injury.endDate?.trim() || null,
              confidence: 1,
            }))
            .filter((injury) => injury.description),
        },
      };
      const response = await onboardingApi.saveMe(payload);
      hydrateState(response.data || {});
      setProfileSaved(true);
      showToast(completeOnboarding ? "Profile finished for coaching" : "Onboarding draft saved");
      return response.data || {};
    } catch (e) {
      showToast(getErrorMessage(e, "Failed to save onboarding profile"), "error");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const persistSmsEndpoint = async (userId, phone) => {
    await messagingEndpointsApi.create({
      userId,
      endpointId: smsEndpointId(phone),
      channel: "SMS",
      externalId: phone,
      status: "ACTIVE",
      primaryEndpoint: messagingChoice === "SMS",
      verifiedAt: new Date().toISOString(),
      metadataJson: JSON.stringify({
        provider: "TWILIO",
        consent: "RECURRING_SMS",
        consentedAt: new Date().toISOString(),
        source: "ONBOARDING",
      }),
    });
  };

  const connectSms = async () => {
    const phone = normalizePhone(user.phone);
    if (!phone) {
      showToast("Add the phone number you want Chara to text", "error");
      return;
    }
    if (!smsConsent) {
      showToast("Accept SMS messaging terms before connecting SMS", "error");
      return;
    }
    const saved = await save(false);
    const userId = saved?.user?.userId || user.userId;
    if (!userId) {
      showToast("Save your profile before connecting SMS", "error");
      return;
    }
    setSmsConnecting(true);
    try {
      await persistSmsEndpoint(userId, phone);
      showToast("SMS connection saved");
    } catch (e) {
      showToast(getErrorMessage(e, "Failed to save SMS connection"), "error");
    } finally {
      setSmsConnecting(false);
    }
  };

  const createTelegramLink = async () => {
    try {
      const response = await onboardingApi.createTelegramLink();
      setTelegramLink(response.data || null);
      showToast("Telegram link ready");
    } catch (e) {
      showToast(getErrorMessage(e, "Failed to create Telegram link"), "error");
    }
  };

  const redeemAccessCode = async () => {
    if (!accessCode.trim()) {
      showToast("Enter a beta access code first", "error");
      return;
    }
    setRedeemingAccess(true);
    try {
      const response = await onboardingApi.redeemAccessCode({ code: accessCode.trim() });
      hydrateState(response.data || {});
      if (response.data?.accessLevel === "ADMIN") {
        showToast("Admin access enabled");
      } else {
        showToast("Beta access recorded");
      }
    } catch (e) {
      showToast(getErrorMessage(e, "Access code was not accepted"), "error");
    } finally {
      setRedeemingAccess(false);
    }
  };

  const addInjury = () => {
    setInjuries((current) => [...current, createInjuryDraft()]);
  };

  const updateInjury = (index, key, value) => {
    setInjuries((current) => current.map((injury, idx) => (idx === index ? { ...injury, [key]: value } : injury)));
  };

  const removeInjury = (index) => {
    setInjuries((current) => {
      const injury = current[index];
      if (!injury?.injuryId) return current.filter((_, idx) => idx !== index);
      return current.map((item, idx) => idx === index
        ? { ...item, status: "resolved", endDate: item.endDate || new Date().toISOString().slice(0, 10) }
        : item);
    });
  };

  const goBack = () => setStepIndex((current) => Math.max(0, current - 1));
  const goNext = () => setStepIndex((current) => Math.min(onboardingSteps.length - 1, current + 1));

  const finishProfile = async () => {
    if (!accessAccepted) {
      showToast("Apply an accepted beta access code before finishing", "error");
      return;
    }
    if (messagingChoice === "SMS" && !smsConsent) {
      showToast("Accept SMS messaging terms before finishing with SMS", "error");
      return;
    }
    const saved = await save(true);
    if (saved && messagingChoice === "SMS") {
      const phone = normalizePhone(user.phone);
      const userId = saved?.user?.userId || user.userId;
      if (phone && userId) {
        try {
          await persistSmsEndpoint(userId, phone);
        } catch (e) {
          showToast(getErrorMessage(e, "Failed to save SMS connection"), "error");
          return;
        }
      }
    }
    if (saved?.onboardingJobGroupId) {
      window.location.assign("/?panel=dashboard");
      return;
    }
    if (saved) window.location.assign("/?panel=dashboard");
  };

  const stepContent = {
    basics: (
      <>
        <div className="section-kicker">Account basics</div>
        <h1>Who are we coaching?</h1>
        <p className="step-copy">Keep this simple. Chara uses these details to start learning your schedule and coaching context.</p>
        <div className="form-grid">
          <Input label="Full name" value={user.name} onChange={(value) => setUser({ ...user, name: value })} />
          <Input label="Phone" value={user.phone} onChange={(value) => setUser({ ...user, phone: value })} />
          <TimeZoneCombo
            value={user.timeZone}
            onChange={(value) => setUser({ ...user, timeZone: value })}
          />
          <Select
            label="Primary distance"
            value={user.preferredDistance}
            onChange={(value) => setUser({ ...user, preferredDistance: value })}
            options={onboardingDistanceOptions}
          />
        </div>
        <div className="consent-stack">
          <label className="consent-check">
            <input
              type="checkbox"
              checked={legalAccepted}
              onChange={(event) => setLegalAccepted(event.target.checked)}
            />
            <span>
              I have read and agree to Chara's <a href="/terms" target="_blank" rel="noreferrer">Terms and Conditions</a> and <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>. I understand Chara is not medical care and does not provide diagnosis, treatment, emergency services, or professional healthcare advice.
            </span>
          </label>
        </div>
      </>
    ),
    background: (
      <>
        <div className="section-kicker">Athlete background</div>
        <h1>Give Chara the real training story.</h1>
        <p className="step-copy">
          Use this as a free-form training resume: what you have done, what has worked, what has gone wrong, and what kind of runner you are right now.
        </p>
        <TextArea
          label="Training experience"
          value={trainingExperienceText}
          onChange={setTrainingExperienceText}
          rows={8}
          placeholder="Example: I have run for 4 years, usually 20-30 miles per week. I trained for one half marathon, handled long runs well, but got shin splints when I added speed too quickly..."
        />
        <Input label="Recent peak weekly mileage" type="number" value={maxMileage} onChange={setMaxMileage} placeholder="45" />
        <p className="field-help">
          Use the most you have run in a single week during the last six months or your most recent real training block. This helps Chara avoid guessing your current load tolerance.
        </p>
        <div className="choice-section">
          <h2>Training bias</h2>
          <p className="step-copy">This tells Chara how aggressively to progress your plan when the tradeoff is fitness versus durability.</p>
          <div className="choice-grid">
            {trainingBiasOptions.map((option) => (
              <OptionCard key={option.value} option={option} selected={trainingBias === option.value} onSelect={() => setTrainingBias(option.value)} />
            ))}
          </div>
        </div>
      </>
    ),
    injuries: (
      <>
        <div className="section-kicker">Injury history</div>
        <h1>What should Chara protect?</h1>
        <p className="step-copy">
          Add anything current, recurring, or relevant. A blank page is fine if you are healthy; this section helps Chara avoid reckless progression.
        </p>
        <div className="injury-list">
          {injuries.map((injury, index) => (
            <div className="injury-row" key={injury.localId}>
              <Input label="Description" value={injury.description} onChange={(value) => updateInjury(index, "description", value)} placeholder="Left Achilles soreness" />
              <Select label="Severity" value={injury.severity} onChange={(value) => updateInjury(index, "severity", value)} options={["mild", "moderate", "severe"]} />
              <Select label="Status" value={injury.status} onChange={(value) => updateInjury(index, "status", value)} options={[
                { value: "new", label: "New" },
                { value: "ongoing", label: "Ongoing" },
                { value: "healing", label: "Healing" },
                { value: "recurring", label: "Recurring" },
                { value: "resolved", label: "Resolved" },
              ]} />
              <Input label="Started" value={injury.startDate} onChange={(value) => updateInjury(index, "startDate", value)} placeholder="2026-04-10 or last month" />
              <Input label="Resolved date" value={injury.endDate} onChange={(value) => updateInjury(index, "endDate", value)} placeholder="Leave blank if unresolved" />
              <div className="injury-row-action">
                {injury.status !== "resolved" && (
                  <Button tone="ghost" onClick={() => removeInjury(index)}>
                    {injury.injuryId ? "Mark resolved" : "Remove"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        <Button tone="secondary" onClick={addInjury}>Add injury</Button>
      </>
    ),
    availability: (
      <>
        <div className="section-kicker">Weekly availability</div>
        <h1>When can training actually happen?</h1>
        <p className="step-copy">
          These are free-form notes for Chara, not calendar rules. Write them like you would tell a coach: time windows, unreliable days, long-run options, commute constraints, or days that should stay protected.
        </p>
        <div className="availability-grid">
          {weekdayOrder.map((day) => (
            <TextArea
              key={day}
              label={day}
              value={availability[day]}
              onChange={(value) => setAvailability((current) => ({ ...current, [day]: value }))}
              rows={3}
              placeholder={availabilityExamples[day]}
            />
          ))}
        </div>
      </>
    ),
    preferences: (
      <>
        <div className="section-kicker">Training preferences</div>
        <h1>What makes training fit your life?</h1>
        <p className="step-copy">
          Preferences are durable soft rules Chara should honor when possible: surfaces, time of day, strength work, group runs, treadmill access, gear limitations, or training patterns that keep you consistent.
        </p>
        <TextArea
          label="Training preferences"
          value={preferencesText}
          onChange={setPreferencesText}
          rows={7}
          placeholder="Example: I like morning runs, mostly run on roads, can get to trails once a week, prefer one strength session, and need simple workouts when work travel picks up."
        />
      </>
    ),
    prs: (
      <>
        <div className="section-kicker">Benchmarks</div>
        <h1>Recent PRs or useful race marks.</h1>
        <p className="step-copy">Optional. These help Chara estimate current fitness, but you can skip them if the clock is not the point right now.</p>
        <div className="form-grid">
          {Object.keys(prTimes).map((distance) => (
            <React.Fragment key={distance}>
              <Input
                label={`${distance} time`}
                value={prTimes[distance]}
                onChange={(value) => setPrTimes((current) => ({ ...current, [distance]: value }))}
                placeholder="19:42 / 1:34:11"
              />
              <Input
                label={`${distance} date`}
                type="date"
                value={prDates[distance] || ""}
                onChange={(value) => setPrDates((current) => ({ ...current, [distance]: value }))}
              />
              <Input
                label={`${distance} context`}
                value={prContexts[distance] || ""}
                onChange={(value) => setPrContexts((current) => ({ ...current, [distance]: value }))}
                placeholder="road race, tune-up, time trial"
              />
            </React.Fragment>
          ))}
        </div>
      </>
    ),
    messaging: (
      <>
        <div className="section-kicker">Messaging</div>
        <h1>Pick where Chara should text you.</h1>
        <p className="step-copy">Choose one channel for onboarding. You can add or switch channels later from settings without losing the saved connection.</p>
        <div className="messaging-choice-grid">
          <button
            type="button"
            className={`messaging-choice-card ${messagingChoice === "TELEGRAM" ? "selected" : ""}`}
            onClick={() => setMessagingChoice("TELEGRAM")}
          >
            <span>Telegram</span>
            <strong>Private bot chat</strong>
            <p>Best while the beta is moving quickly. Open the bot once and Chara can reply there.</p>
          </button>
          <button
            type="button"
            className={`messaging-choice-card ${messagingChoice === "SMS" ? "selected" : ""}`}
            onClick={() => setMessagingChoice("SMS")}
          >
            <span>SMS</span>
            <strong>Standard text messaging</strong>
            <p>Use your phone number for Twilio SMS. We will verify delivery when SMS access is enabled.</p>
          </button>
        </div>
        {messagingChoice === "TELEGRAM" ? (
          <>
        <div className="telegram-actions">
          <Button tone="secondary" onClick={createTelegramLink}>Generate Telegram link</Button>
          <Button onClick={() => save(false)} loading={saving}>Save draft</Button>
        </div>
        {telegramLink ? (
          <div className="telegram-link-card">
            <div className="telegram-step-list">
              <div className="telegram-step">
                <span>1</span>
                <div>
                  <strong>Open the bot</strong>
                  <a className="telegram-link" href={telegramLink.setupUrl} target="_blank" rel="noreferrer">
                    Open @{telegramLink.botUsername} in Telegram
                  </a>
                </div>
              </div>
              <div className="telegram-step">
                <span>2</span>
                <div>
                  <strong>Send the start message</strong>
                  <p>The link should prefill the setup code. Send the message so Chara can claim this account.</p>
                </div>
              </div>
              <div className="telegram-step">
                <span>3</span>
                <div>
                  <strong>Wait for connected</strong>
                  <p>This page checks every few seconds and should update automatically.</p>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <div className={`telegram-status ${telegramStatus?.connected ? "connected" : "pending"}`}>
          <strong>{telegramStatus?.connected ? "Telegram connected" : "Telegram not connected yet"}</strong>
          <span>
            {telegramStatus?.connected
              ? `Endpoint ${telegramStatus.endpoint?.endpointId || ""}`
              : "You can keep going without Telegram, but messaging will not work until it is connected."}
          </span>
        </div>
          </>
        ) : (
          <div className="sms-connect-card">
            <Input
              label="Mobile phone"
              value={user.phone || ""}
              onChange={(value) => setUser({ ...user, phone: value })}
              placeholder="+1 555 123 4567"
            />
            <label className="sms-consent-check">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(event) => setSmsConsent(event.target.checked)}
              />
              <span>
                I agree to receive recurring SMS messages from Chara about my running plan, workout reminders, coaching follow-ups, schedule updates, onboarding, account updates, and support. Message frequency varies. Message and data rates may apply. Reply STOP to opt out and HELP for help. Consent is not required to create an account.
              </span>
            </label>
            <div className="telegram-actions">
              <Button tone="secondary" onClick={connectSms} loading={smsConnecting} disabled={!user.phone || !smsConsent}>
                Save SMS connection
              </Button>
              <Button onClick={() => save(false)} loading={saving}>Save draft</Button>
            </div>
            <div className="telegram-status pending">
              <strong>SMS will be ready when Twilio access is enabled</strong>
              <span>We will keep this phone connection on your account so you can switch preferred messaging later.</span>
            </div>
          </div>
        )}
      </>
    ),
  };

  const pageContent = loading ? (
    <div className="onboarding-loading">Loading your onboarding profile…</div>
  ) : loadError ? (
    <div className="onboarding-card onboarding-error" role="alert">
      <div className="section-kicker">Could not load profile</div>
      <h2>We stopped before editing anything.</h2>
      <p className="hero-text">{loadError}</p>
      <div className="telegram-actions">
        <Button onClick={load}>Retry</Button>
      </div>
    </div>
  ) : !accessAccepted ? (
    <BetaAccessGate
      accessCode={accessCode}
      setAccessCode={setAccessCode}
      redeemAccessCode={redeemAccessCode}
      redeemingAccess={redeemingAccess}
      email={authUser?.email || ""}
    />
  ) : (
    <section className="onboarding-wizard">
      <div className="wizard-progress" aria-label="Onboarding progress">
        <div className="progress-label">
          <span>{currentStep.label}</span>
          <strong>{completion}%</strong>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${completion}%` }} />
        </div>
        <div className="wizard-step-list">
          {onboardingSteps.map((step, index) => (
            <button
              key={step.key}
              className={`wizard-step-pill ${index === stepIndex ? "active" : ""} ${index < stepIndex ? "complete" : ""}`}
              onClick={() => setStepIndex(index)}
              type="button"
            >
              <span className="wizard-step-index">{index + 1}</span>
              <span className="wizard-step-label">{step.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="wizard-card">
        <MascotSprite variant={currentStep.mascot} className="wizard-mascot" />
        <div className="wizard-card-content">{stepContent[currentStep.key]}</div>
        <div className="wizard-actions">
          <Button tone="ghost" onClick={goBack} disabled={stepIndex === 0}>Back</Button>
          <div className="wizard-actions-right">
            <Button tone="secondary" onClick={() => save(false)} loading={saving}>Save draft</Button>
            {stepIndex === onboardingSteps.length - 1 ? (
              <Button onClick={finishProfile} disabled={!canFinishProfile} loading={saving}>Finish profile</Button>
            ) : (
              <Button onClick={goNext}>Next</Button>
            )}
          </div>
        </div>
      </div>
      <p className="wizard-next">{nextStep}</p>
    </section>
  );

  if (isLoading) {
    return (
      <div className="onboarding-shell">
        <div className="onboarding-loading">Checking your session…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="onboarding-shell">
        <div className="onboarding-topbar">
          <div className="ui-tag">Beta onboarding</div>
          <a href="/" className="back-link">Back to home</a>
        </div>
        <section className="onboarding-wizard">
          <div className="wizard-card wizard-card--auth">
            <div className="ui-tag">Closed beta</div>
            <MascotSprite variant="wave" className="wizard-mascot" />
            <h1>Chara is opening the starting line carefully.</h1>
            <p className="step-copy">
              Have an invite? Create your account, enter the private beta code, and then tell Chara the context a real coach would need. No code yet? Join the interest list without creating an account.
            </p>
            <div className="telegram-actions">
              <Button
                onClick={() =>
                  loginWithRedirect(auth0LoginParams({ screenHint: "signup", redirectPath: "/onboarding" }))
                }
                disabled={!signupLegalAccepted}
              >
                Sign up
              </Button>
              <Button
                tone="secondary"
                onClick={() => loginWithRedirect(auth0LoginParams({ redirectPath: "/onboarding" }))}
              >
                Log in
              </Button>
            </div>
            <label className="consent-check consent-check--public">
              <input
                type="checkbox"
                checked={signupLegalAccepted}
                onChange={(event) => setSignupLegalAccepted(event.target.checked)}
              />
              <span>
                I agree to Chara's <a href="/terms" target="_blank" rel="noreferrer">Terms</a> and <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and understand that Chara is AI-assisted fitness coaching, not medical care.
              </span>
            </label>
            <div className="beta-public-waitlist">
              <div className="section-kicker">No invite yet?</div>
              <h2>Join the beta waitlist.</h2>
              <p className="step-copy">Leave your email and we will reach out as the next spots open.</p>
              <WaitlistForm />
            </div>
          </div>
        </section>
        <LegalLinkFooter />
        <Toast toast={toast} onClose={() => setToast(null)} />
      </div>
    );
  }

  if (isBuildingProfile) {
    return (
      <div className="onboarding-shell onboarding-shell--building">
        <div className="onboarding-topbar">
          <div className="ui-tag">Coach build</div>
          <div className="auth-toolbar">
            <div>
              <div className="auth-toolbar-label">Signed in</div>
              <div className="auth-toolbar-value">{authUser?.email || authUser?.name || "Authenticated runner"}</div>
            </div>
          </div>
        </div>
        <section className="coach-build-stage">
          <div className="coach-build-orbit" aria-hidden="true">
            <MascotSprite variant="run" className="coach-build-mascot coach-build-mascot--run" alt="" />
            <MascotSprite variant="jump" className="coach-build-mascot coach-build-mascot--jump" alt="" />
            <MascotSprite variant="wave" className="coach-build-mascot coach-build-mascot--wave" alt="" />
          </div>
          <div className="coach-build-copy">
            <div className="section-kicker">Creating athlete profile</div>
            <h1>Your coach is warming up.</h1>
            <p>{buildError || buildStatus}</p>
            {buildError ? (
              <div className="telegram-actions">
                <Button onClick={() => window.location.assign("/onboarding")}>Back to onboarding</Button>
              </div>
            ) : (
              <>
                <div className="coach-build-meter" aria-label="Coach profile creation in progress">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="coach-build-status-list" aria-label="Coach profile build steps">
                  {coachBuildSignals.map((signal) => <span key={signal}>{signal}</span>)}
                </div>
              </>
            )}
          </div>
        </section>
        <Toast toast={toast} onClose={() => setToast(null)} />
        <LegalLinkFooter />
      </div>
    );
  }

  return (
    <div className="onboarding-shell">
      <div className="onboarding-topbar">
        <div className="ui-tag">Beta onboarding</div>
        <div className="auth-toolbar">
          <div>
            <div className="auth-toolbar-label">Signed in</div>
            <div className="auth-toolbar-value">{authUser?.email || authUser?.name || "Authenticated runner"}</div>
          </div>
          <button
            className="auth-logout-button"
            onClick={() =>
              logout({
                logoutParams: {
                  returnTo: `${window.location.origin}/onboarding`,
                },
              })
            }
          >
            Log out
          </button>
        </div>
      </div>
      {pageContent}
      <LegalLinkFooter />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

function OnboardingConfigMissing() {
  return (
    <div className="onboarding-shell">
      <div className="onboarding-loading">
        Set `REACT_APP_AUTH0_DOMAIN`, `REACT_APP_AUTH0_CLIENT_ID`, and `REACT_APP_AUTH0_AUDIENCE` before enabling onboarding.
      </div>
    </div>
  );
}

function OnboardingShellWithAuth() {
  const auth = useAuth0();
  return <OnboardingShell auth={auth} />;
}

function OnboardingShellWithDevPreview({ devPreview }) {
  const auth = {
    isAuthenticated: true,
    isLoading: false,
    user: devPreview.user,
    loginWithRedirect: () => undefined,
    logout: () => window.location.assign("/onboarding"),
    getAccessTokenSilently: async () => null,
  };
  return (
    <>
      <div className="dev-preview-banner">{devPreview.label}</div>
      <OnboardingShell auth={auth} />
    </>
  );
}

export default function OnboardingPage({ auth0Configured = false, devPreview = null }) {
  if (devPreview?.isActive) {
    return <OnboardingShellWithDevPreview devPreview={devPreview} />;
  }
  if (!auth0Configured) {
    return <OnboardingConfigMissing />;
  }
  return <OnboardingShellWithAuth />;
}
